import type { CostsResponse } from '@zinc/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { Bar, Panel, smallButton, tableCell, usd, when } from './parts';

/** Admin → API and costs: the monthly cap, spend so far, per purpose, per month, the calls. */
export function AdminCostsPage() {
  const client = useQueryClient();
  const costs = useQuery({
    queryKey: ['admin', 'costs'],
    queryFn: () => api<CostsResponse>('/api/admin/costs'),
  });
  const [cap, setCap] = useState('');
  const save = useMutation({
    mutationFn: (v: number) => api('/api/admin/costs/cap', { method: 'PUT', json: { usd: v } }),
    onSuccess: () => {
      setCap('');
      void client.invalidateQueries({ queryKey: ['admin'] });
    },
  });
  const d = costs.data;
  const max = Math.max(0.01, ...(d?.months ?? []).map((m) => m.usd), d?.capUsd ?? 0);
  return (
    <>
      <h1 className="m-0 text-[26px] font-bold">API and costs</h1>
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="This month" small>
          <div className="flex items-baseline gap-1.5">
            <span className="text-[32px] font-bold tabular-nums">{usd(d?.monthUsd ?? 0)}</span>
            <span className="text-[14px] text-muted">of {usd(d?.capUsd ?? 0)} cap</span>
          </div>
          <Bar value={d ? d.monthUsd / Math.max(d.capUsd, 0.01) : 0} />
          {(d?.byPurpose ?? []).map((p) => (
            <div key={p.purpose} className="flex justify-between text-[13px]">
              <span>
                {p.purpose === 'rule-reading-batch'
                  ? 'Rule reading (batch, half price)'
                  : 'Rule reading'}{' '}
                · {p.calls} calls
              </span>
              <span className="tabular-nums">{usd(p.usd)}</span>
            </div>
          ))}
          <p className="m-0 text-[13px] text-muted">
            Rule reader: {d?.ruleReader.model ?? '…'}
            {d && !d.ruleReader.configured && ' · no API key on the worker: unmapped rules wait'}
          </p>
        </Panel>
        <Panel title="Monthly cap" small>
          <form
            className="flex flex-wrap items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              const v = Number(cap);
              if (Number.isFinite(v) && v >= 0) save.mutate(v);
            }}
          >
            <label htmlFor="cap" className="text-[14px]">
              Cap in US dollars
            </label>
            <input
              id="cap"
              inputMode="decimal"
              value={cap}
              placeholder={d ? d.capUsd.toFixed(2) : ''}
              onChange={(e) => setCap(e.target.value)}
              className="h-10 w-28 rounded-btn border border-line bg-surface px-3 font-sans text-[14px] text-ink"
            />
            <button
              type="submit"
              disabled={save.isPending || cap.trim() === ''}
              className={smallButton}
            >
              Save
            </button>
          </form>
          <p className="m-0 text-[13px] text-muted">
            Jobs that would go over the cap pause until next month. Raising it lets them go on.
          </p>
          <div className="flex h-24 items-end gap-2" aria-label="Spend per month">
            {(d?.months ?? []).map((m) => (
              <div key={m.month} className="flex flex-1 flex-col items-center gap-1">
                <div
                  className="w-full rounded-t-[4px] bg-accent"
                  style={{ height: `${(m.usd / max) * 64}px` }}
                />
                <span className="text-[11px] text-muted">{m.month.slice(5)}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>
      <Panel title="Calls">
        <div className="-mx-1 overflow-x-auto px-1">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="text-left text-muted">
                <th className={`${tableCell} font-semibold`}>When</th>
                <th className={`${tableCell} font-semibold`}>Job</th>
                <th className={`${tableCell} font-semibold`}>Model</th>
                <th className={`${tableCell} text-right font-semibold`}>In / cached / out</th>
                <th className={`${tableCell} text-right font-semibold`}>Cost</th>
              </tr>
            </thead>
            <tbody>
              {(d?.calls ?? []).map((c) => (
                <tr key={c.id}>
                  <td className={tableCell}>{when(c.createdAt)}</td>
                  <td className={tableCell}>{c.batch ? `${c.purpose}` : c.purpose}</td>
                  <td className={tableCell}>{c.model}</td>
                  <td className={`${tableCell} text-right tabular-nums`}>
                    {c.inputTokens + c.cacheWriteTokens} / {c.cacheReadTokens} / {c.outputTokens}
                  </td>
                  <td className={`${tableCell} text-right tabular-nums`}>
                    ${c.costUsd.toFixed(4)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {d?.calls.length === 0 && <span className="text-[13px] text-muted">No calls yet.</span>}
      </Panel>
    </>
  );
}
